// src/components/crm/CustomFieldsSettings.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { Plus, Trash2, AlignLeft, Calendar, Hash } from 'lucide-react';

const CustomFieldsSettings = ({ currentUserProfile }) => {
    const [customFields, setCustomFields] = useState([]);
    const [newFieldLabel, setNewFieldLabel] = useState('');
    const [newFieldType, setNewFieldType] = useState('text');
    const [isLoading, setIsLoading] = useState(true);

    const settingsDocRef = doc(db, `organizations/${currentUserProfile.organizationId}/settings`, 'customerFields');

    useEffect(() => {
        const unsubscribe = onSnapshot(settingsDocRef, (doc) => {
            if (doc.exists()) { setCustomFields(doc.data().fields || []); }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [settingsDocRef]);

    const handleAddField = async (e) => {
        e.preventDefault();
        if (!newFieldLabel.trim()) return;

        const newField = {
            id: newFieldLabel.toLowerCase().replace(/\s+/g, '_'), 
            label: newFieldLabel.trim(),
            type: newFieldType,
        };

        if (customFields.some(f => f.id === newField.id)) return alert('Field name already exists.');

        const updatedFields = [...customFields, newField];
        await setDoc(settingsDocRef, { fields: updatedFields }, { merge: true });
        setNewFieldLabel('');
        setNewFieldType('text');
    };

    const handleDeleteField = async (fieldId) => {
        if (window.confirm('Delete this field? Data stored in this field for clients will be lost.')) {
            const updatedFields = customFields.filter(f => f.id !== fieldId);
            await setDoc(settingsDocRef, { fields: updatedFields }, { merge: true });
        }
    };

    const getIcon = (type) => {
        switch(type) {
            case 'number': return <Hash size={16} className="text-blue-500"/>;
            case 'date': return <Calendar size={16} className="text-orange-500"/>;
            default: return <AlignLeft size={16} className="text-gray-500"/>;
        }
    }

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-2 text-gray-800">Custom Client Fields</h2>
            <p className="text-sm text-gray-500 mb-6">Capture specific info like "Gate Code" or "Dog Name".</p>

            {/* ADD FORM (Stacked on Mobile) */}
            <form onSubmit={handleAddField} className="flex flex-col sm:flex-row items-end gap-3 p-4 border rounded-xl bg-gray-50 mb-6">
                <div className="w-full flex-grow">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Label</label>
                    <input
                        type="text"
                        value={newFieldLabel}
                        onChange={(e) => setNewFieldLabel(e.target.value)}
                        placeholder="e.g. Lawn Size"
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
                <div className="w-full sm:w-40 shrink-0">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                    <select
                        value={newFieldType}
                        onChange={(e) => setNewFieldType(e.target.value)}
                        className="w-full p-2.5 border border-gray-300 rounded-lg bg-white outline-none"
                    >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                    </select>
                </div>
                <button type="submit" className="w-full sm:w-auto flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-sm transition-colors">
                    <Plus size={18} className="mr-2" /> Add
                </button>
            </form>

            {/* FIELD LIST */}
            <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">Existing Fields</h3>
                {isLoading ? <p className="text-center text-gray-500 text-sm py-4">Loading...</p> :
                    customFields.length > 0 ? (
                        <ul className="space-y-2">
                            {customFields.map(field => (
                                <li key={field.id} className="p-3 flex justify-between items-center border rounded-lg hover:border-blue-300 transition-colors bg-white">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-50 rounded-md border border-gray-100">
                                            {getIcon(field.type)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800 text-sm">{field.label}</p>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide font-bold">{field.type}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => handleDeleteField(field.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                        <Trash2 size={18} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-sm">
                            No custom fields created yet.
                        </p>
                    )
                }
            </div>
        </div>
    );
};

export default CustomFieldsSettings;